import { useEffect, type DependencyList, type RefObject } from "react";

/**
 * Keeps the height of `ref` in a CSS variable on the closest `.schedule-component`, so the
 * schedule panels can subtract it from their height (see `--schedule-content-height`).
 * Pass `deps` when the element can mount later than the calling component (e.g. behind a spinner).
 */
export const useHeightCssVariable = (
    ref: RefObject<HTMLElement>,
    variable: string,
    deps: DependencyList = []
) => {
    useEffect(() => {
        const element = ref.current;
        const container = element?.closest<HTMLElement>(".schedule-component");
        if (!element || !container) return;

        // Update in the next frame (and only when the height actually changed) - resizing the panels
        // synchronously inside the observer callback triggers "ResizeObserver loop" errors
        let frame = 0;
        let lastHeight = -1;
        const resizeObserver = new ResizeObserver(() => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
                const height = element.offsetHeight;
                if (height === lastHeight) return;
                lastHeight = height;
                container.style.setProperty(variable, `${height}px`);
            });
        });
        resizeObserver.observe(element);

        return () => {
            cancelAnimationFrame(frame);
            resizeObserver.disconnect();
            container.style.removeProperty(variable);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref, variable, ...deps]);
};
